const RE_ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const hasValue = (value) => value !== null && value !== undefined && value !== '';
const firstNonNullish = (...candidates) => candidates.find((value) => value !== null && value !== undefined);
const toDisplayTextOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text || null;
};
const toFiniteNumberOrNull = (value) => {
  if (!hasValue(value)) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};
const coerceIsoDatePart = (value) => {
  if (!hasValue(value)) return null;
  const datePart = String(value).split('T')[0].split(' ')[0];
  return RE_ISO_DATE.test(datePart) ? datePart : null;
};
const firstArray = (candidates) => {
  const rows = (Array.isArray(candidates) ? candidates : []).find(Array.isArray);
  return Array.isArray(rows) ? rows : [];
};
const getNestedValue = (obj, path) => {
  if (!obj || typeof obj !== 'object' || !path) return undefined;
  return String(path).split('.')
    .filter(Boolean)
    .reduce((acc, key) => (acc && typeof acc === 'object' ? acc[key] : undefined), obj);
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatApiDate = (value) => {
  if (!value || value === '9999-01-01') return '-';
  const parts = String(value).split('-');
  if (parts.length !== 3) return String(value);
  const [year, month, day] = parts;
  const monthIndex = Number(month) - 1;
  if (!MONTH_NAMES[monthIndex]) return String(value);
  return `${day}-${MONTH_NAMES[monthIndex]}-${year}`;
};

const getDayNameForApiDate = (value) => {
  const datePart = coerceIsoDatePart(value);
  if (!datePart) return '';
  return DAYS[new Date(datePart).getDay()] || '';
};

const normalizeText = (value) => String(value || '').trim()
  .toLowerCase();

const isOverdueRiskRow = (targetDateValue, statusValue) => {
  const targetDatePart = coerceIsoDatePart(targetDateValue);
  if (!targetDatePart) return false;
  if (normalizeText(statusValue) === 'closed') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(targetDatePart).getTime() < today.getTime();
};

const isIssueRiskEntity = (entityType) => entityType === 'issue';
const isInfoSecRiskEntity = (entityType) => entityType === 'infosec';
const RISK_ID_PREFIX_BY_ENTITY = {
  risk: 'RR',
  issue: 'IL',
  infosec: 'IR',
};
const normalizeRiskDisplayId = (value, entityType) => {
  if (!hasValue(value)) return value;
  const normalizedValue = String(value).trim();
  const prefix = RISK_ID_PREFIX_BY_ENTITY[entityType];
  return prefix && /^\d+$/.test(normalizedValue) ? `${prefix}-${normalizedValue}` : value;
};
const getRiskDisplayId = (row, index, entityType) => {
  const fallbackId = `${entityType.toUpperCase()}-${index + 1}`;
  if (isIssueRiskEntity(entityType)) {
    return firstNonNullish(row?.issue_no, row?.issue_id, row?.id, row?.row_id, fallbackId);
  }
  if (isInfoSecRiskEntity(entityType)) {
    return firstNonNullish(row?.infosec_no, row?.risk_no, row?.infosec_id, row?.id, row?.row_id, fallbackId);
  }
  return firstNonNullish(row?.risk_no, row?.risk_id, row?.id, row?.row_id, fallbackId);
};

const getRiskScoreFromRow = (row, entityType) => {
  const likelihood = toFiniteNumberOrNull(row?.likelihood_value);
  const impact = toFiniteNumberOrNull(row?.impact_value);
  const computed = (likelihood === null || impact === null) ? null : likelihood * impact;

  if (!isInfoSecRiskEntity(entityType) && computed !== null) return computed;

  const riskScore = toFiniteNumberOrNull(row?.risk_score);
  if (riskScore !== null) return riskScore;

  return computed;
};

const getPriorityByScore = (score) => {
  if (score === null) return null;
  if (score >= 15) return 'High';
  if (score >= 6) return 'Medium';
  return 'Low';
};

const getRiskScoreLevel = (score, entityType) => {
  if (score === null) return null;

  if (isInfoSecRiskEntity(entityType)) {
    if (score > 296) return 'High';
    if (score >= 108) return 'Medium';
    return 'Low';
  }

  if (score > 8) return 'High';
  if (score >= 4) return 'Medium';
  if (score >= 1) return 'Low';
  return null;
};

const getRiskStatusText = (row, entityType) => {
  if (isIssueRiskEntity(entityType)) {
    return firstNonNullish(toDisplayTextOrNull(row?.issue_status_name), '-');
  }
  if (isInfoSecRiskEntity(entityType)) {
    return firstNonNullish(
      toDisplayTextOrNull(row?.risk_status_name),
      toDisplayTextOrNull(row?.infosec_status_name),
      toDisplayTextOrNull(row?.status),
      '-',
    );
  }
  return firstNonNullish(
    toDisplayTextOrNull(row?.issue_status_name),
    toDisplayTextOrNull(row?.risk_status_name),
    toDisplayTextOrNull(row?.risk_status),
    toDisplayTextOrNull(row?.status),
    '-',
  );
};

const getRiskDescription = (row, entityType) => {
  if (isIssueRiskEntity(entityType)) {
    return firstNonNullish(
      toDisplayTextOrNull(row?.issue_description),
      toDisplayTextOrNull(row?.issue_no),
      '-',
    );
  }
  if (isInfoSecRiskEntity(entityType)) {
    return firstNonNullish(
      toDisplayTextOrNull(row?.infosec_description),
      toDisplayTextOrNull(row?.risk_description),
      toDisplayTextOrNull(row?.infosec_no),
      toDisplayTextOrNull(row?.risk_no),
      '-',
    );
  }
  return firstNonNullish(toDisplayTextOrNull(row?.risk_description), toDisplayTextOrNull(row?.risk_no), '-');
};

const getRiskCategory = (row, entityType) => {
  if (isIssueRiskEntity(entityType)) return firstNonNullish(toDisplayTextOrNull(row?.issue_category_name), '-');
  if (isInfoSecRiskEntity(entityType)) {
    return firstNonNullish(
      toDisplayTextOrNull(row?.asset_category_name),
      toDisplayTextOrNull(row?.category_name),
      '-',
    );
  }
  return firstNonNullish(toDisplayTextOrNull(row?.category_name), toDisplayTextOrNull(row?.category), '-');
};

const getRiskAssetCategory = (row, entityType) => (
  isInfoSecRiskEntity(entityType)
    ? firstNonNullish(
      toDisplayTextOrNull(row?.asset_category_name),
      toDisplayTextOrNull(row?.category_name),
      '-',
    )
    : firstNonNullish(toDisplayTextOrNull(row?.category_name), '-')
);

const getRiskDatesRaw = (row) => ({
  raised: firstNonNullish(
    row?.raised_on_date,
    row?.risk_raised_date,
    row?.raisedOnDate,
    row?.raised_on,
    row?.raised_date,
    row?.created_at,
    row?.createdAt,
  ),
  target: firstNonNullish(
    row?.target_date,
    row?.targetDate,
    row?.target_dates,
    row?.targetDates,
  ),
  revised: firstNonNullish(
    row?.revised_target_date,
    row?.revisedTargetDate,
    row?.revised_targetDate,
    row?.revised_target_dates,
    row?.revisedTargetDates,
    row?.revisedDate,
    row?.revised_date,
  ),
  lastUpdated: firstNonNullish(
    row?.risk_last_updated_date,
    row?.issue_last_updated_date,
    row?.last_updated_date,
    row?.lastUpdatedDate,
    row?.updated_on,
    row?.updated_at,
    row?.last_updated,
    row?.lastUpdated,
  ),
});

const getRiskIds = (row, index, entityType) => ({
  id: normalizeRiskDisplayId(getRiskDisplayId(row, index, entityType), entityType),
  row_id: isIssueRiskEntity(entityType)
    ? firstNonNullish(row?.issue_id, row?.row_id, row?.id, null)
    : firstNonNullish(row?.infosec_id, row?.issue_id, row?.risk_id, row?.row_id, row?.id, null),
});

const mapRiskRowToUi = (row, index, entityType) => {
  const statusText = getRiskStatusText(row, entityType);
  const score = getRiskScoreFromRow(row, entityType);
  const dates = getRiskDatesRaw(row);
  const identifiers = getRiskIds(row, index, entityType);
  const isIssueEntity = isIssueRiskEntity(entityType);

  return {
    ...identifiers,
    _entity_type: entityType,
    status: statusText,
    statusNormalized: normalizeText(statusText),
    description: getRiskDescription(row, entityType),
    category: getRiskCategory(row, entityType),
    assetCategory: getRiskAssetCategory(row, entityType),
    category_id: firstNonNullish(
      row?.category_id,
      row?.categoryId,
      row?.asset_category_id,
      row?.assetCategoryId,
      row?.issue_category_id,
      row?.issueCategoryId,
      null,
    ),
    riskScore: isIssueEntity
      ? (score === null ? '-' : String(Number(score.toFixed(2))))
      : firstNonNullish(getRiskScoreLevel(score, entityType), '-'),
    riskScoreValue: score,
    risk_score: row?.risk_score,
    likelihood_value: row?.likelihood_value,
    impact_value: row?.impact_value,
    likelihood: firstNonNullish(
      toDisplayTextOrNull(row?.likelihood_name),
      toDisplayTextOrNull(row?.likelihood_id),
      '-',
    ),
    likelihood_id: firstNonNullish(row?.likelihood_id, row?.likelihoodId, null),
    impact: firstNonNullish(
      toDisplayTextOrNull(row?.impact_name),
      toDisplayTextOrNull(row?.impact_id),
      '-',
    ),
    impact_id: firstNonNullish(row?.impact_id, row?.impactId, null),
    risk_status_id: firstNonNullish(
      row?.risk_status_id,
      row?.riskStatusId,
      row?.infosec_status_id,
      row?.infosecStatusId,
      row?.issue_status_id,
      row?.issueStatusId,
      row?.status_id,
      row?.statusId,
      null,
    ),
    priority: firstNonNullish(toDisplayTextOrNull(row?.priority), getPriorityByScore(score), '-'),
    owner: firstNonNullish(toDisplayTextOrNull(row?.owner), toDisplayTextOrNull(row?.assigned_to), '-'),
    assignedTo: isIssueEntity
      ? firstNonNullish(toDisplayTextOrNull(row?.assigned_to), '-')
      : firstNonNullish(
        toDisplayTextOrNull(row?.assigned_to),
        toDisplayTextOrNull(row?.owner),
        '-',
      ),
    raisedDate: formatApiDate(dates.raised),
    raisedDay: getDayNameForApiDate(dates.raised) || '-',
    raised_on_date: dates.raised,
    targetDate: formatApiDate(dates.target),
    target_date: dates.target,
    revisedDate: formatApiDate(dates.revised),
    revised_target_date: dates.revised,
    mitigation: firstNonNullish(toDisplayTextOrNull(row?.mitigation_strategy_value), '-'),
    mitigation_strategy_id: firstNonNullish(
      row?.mitigation_strategy_id,
      row?.mitigationStrategyId,
      null,
    ),
    measures: firstNonNullish(
      toDisplayTextOrNull(row?.measures_taken),
      toDisplayTextOrNull(row?.risk_treatment),
      toDisplayTextOrNull(row?.status_update),
      '-',
    ),
    statusUpdates: isIssueEntity
      ? firstNonNullish(toDisplayTextOrNull(row?.status_update), '-')
      : firstNonNullish(
        toDisplayTextOrNull(row?.status_update),
        toDisplayTextOrNull(row?.risk_treatment),
        '-',
      ),
    lastUpdated: isIssueEntity
      ? formatApiDate(row?.issue_last_updated_date)
      : formatApiDate(
        firstNonNullish(row?.last_updated_date, row?.risk_last_updated_date, dates.lastUpdated),
      ),
    last_updated_date: isIssueEntity
      ? firstNonNullish(row?.issue_last_updated_date, dates.lastUpdated)
      : firstNonNullish(row?.last_updated_date, row?.risk_last_updated_date, dates.lastUpdated),
    lastUpdatedDay: getDayNameForApiDate(dates.lastUpdated) || '-',
    issue_status_name: firstNonNullish(toDisplayTextOrNull(row?.issue_status_name), statusText),
    risk_status_name: firstNonNullish(
      toDisplayTextOrNull(row?.issue_status_name),
      toDisplayTextOrNull(row?.risk_status_name),
      statusText,
    ),
    issue_status_id: firstNonNullish(
      row?.issue_status_id,
      row?.issueStatusId,
      row?.risk_status_id,
      row?.riskStatusId,
      row?.status_id,
      row?.statusId,
      null,
    ),
    issue_category_id: firstNonNullish(
      row?.issue_category_id,
      row?.issueCategoryId,
      row?.category_id,
      row?.categoryId,
      null,
    ),
    assigned_to: firstNonNullish(row?.assigned_to, row?.assignedTo, row?.owner, null),
    status_update: firstNonNullish(
      row?.status_update,
      row?.statusUpdates,
      row?.risk_treatment,
      null,
    ),
    isOverdue: isOverdueRiskRow(dates.target, statusText),
  };
};

const getRawRiskCategoryRowsFromPayload = (payload) => {
  const nested = payload?.riskRegister ?? payload?.risk_register ?? payload?.risk ?? null;
  return firstArray([
    payload?.risk_categories,
    payload?.riskCategories,
    payload?.risk_category,
    payload?.riskCategory,
    payload?.risk_category_master,
    payload?.riskCategoryMaster,
    nested?.risk_categories,
    nested?.riskCategories,
    nested?.risk_category_master,
  ]);
};

const getRiskAssetRows = (payload) => {
  const nested = payload?.riskRegister ?? payload?.risk_register ?? payload?.risk ?? null;
  return firstArray([
    payload?.riskAssetCategoryOptions,
    payload?.assetCategoryOptions,
    payload?.asset_category_master,
    payload?.assetCategoryMaster,
    nested?.riskAssetCategoryOptions,
    nested?.assetCategoryOptions,
    nested?.asset_category_master,
  ]);
};

const getRawRiskImpactRowsFromPayload = (payload) => {
  const nested = payload?.riskRegister ?? payload?.risk_register ?? payload?.risk ?? null;
  return firstArray([
    payload?.impact_master,
    payload?.impactMaster,
    payload?.risk_impact_master,
    payload?.riskImpactMaster,
    nested?.impact_master,
    nested?.impactMaster,
    nested?.risk_impact_master,
  ]);
};

const getRawRiskLikelihoodRowsFromPayload = (payload) => {
  const nested = payload?.riskRegister ?? payload?.risk_register ?? payload?.risk ?? null;
  return firstArray([
    payload?.likelihood_master,
    payload?.likelihoodMaster,
    payload?.risk_likelihood_master,
    payload?.riskLikelihoodMaster,
    nested?.likelihood_master,
    nested?.likelihoodMaster,
    nested?.risk_likelihood_master,
  ]);
};

const getRawRiskMitigationRowsFromPayload = (payload) => {
  const nested = payload?.riskRegister ?? payload?.risk_register ?? payload?.risk ?? null;
  return firstArray([
    payload?.mitigation_strategy_master,
    payload?.mitigationStrategyMaster,
    payload?.risk_mitigation_master,
    payload?.riskMitigationMaster,
    nested?.mitigation_strategy_master,
    nested?.mitigationStrategyMaster,
    nested?.risk_mitigation_master,
  ]);
};

const getRawRiskStatusRowsFromPayload = (payload) => {
  const nested = payload?.riskRegister ?? payload?.risk_register ?? payload?.risk ?? null;
  return firstArray([
    payload?.risk_status_master,
    payload?.riskStatusMaster,
    payload?.status_master,
    payload?.statusMaster,
    nested?.risk_status_master,
    nested?.riskStatusMaster,
    nested?.status_master,
  ]);
};

const getRawIssueStatusRowsFromPayload = (payload) => {
  const nested = payload?.riskRegister ?? payload?.risk_register ?? payload?.risk ?? null;
  return firstArray([
    payload?.issueStatusMaster,
    payload?.issueStatusOptions,
    payload?.issue_status_master,
    payload?.issueStatusMaster,
    nested?.issue_status_master,
    nested?.issueStatusMaster,
  ]);
};

const getRawIssueCategoryRowsFromPayload = (payload) => {
  const nested = payload?.riskRegister ?? payload?.risk_register ?? payload?.risk ?? null;
  return firstArray([
    payload?.issueCategoryMaster,
    payload?.issueCategoryOptions,
    payload?.issue_category_master,
    payload?.issueCategoryMaster,
    nested?.issue_category_master,
    nested?.issueCategoryMaster,
  ]);
};

const buildRiskOptionsFromMaster = ({
  masterRows,
  fallbackRows = [],
  idKeys = [],
  nameKeys = [],
}) => {
  const options = [];
  const seen = new Set();

  const addOption = (idValue, nameValue, row = {}) => {
    if (!hasValue(idValue) || !hasValue(nameValue)) return;
    const key = String(idValue);
    if (seen.has(key)) return;
    seen.add(key);
    options.push({
      ...row,
      value: idValue,
      label: String(nameValue),
      totalAssetValue: row?.totalAssetValue ?? row?.total_asset_value,
    });
  };

  const extract = (row) => {
    const idValue = firstNonNullish(...idKeys.map((key) => row?.[key]));
    const nameValue = firstNonNullish(...nameKeys.map((key) => row?.[key]));
    addOption(idValue, nameValue, row);
  };

  (Array.isArray(masterRows) ? masterRows : [])
    .filter((row) => row && typeof row === 'object')
    .forEach(extract);
  (Array.isArray(fallbackRows) ? fallbackRows : [])
    .filter((row) => row && typeof row === 'object')
    .forEach(extract);

  return options;
};

const RISK_OPTION_BUILDERS = [
  {
    key: 'riskAssetCategoryOptions',
    getMasterRows: getRiskAssetRows,
    getFallbackRows: ({ infoSecRisks }) => infoSecRisks,
    idKeys: ['id', 'value', 'asset_category_id', 'assetCategoryId', 'category_id', 'categoryId'],
    nameKeys: [
      'asset_category_element', 'assetCategoryElement', 'asset_category_name',
      'assetCategoryName', 'category_name', 'categoryName', 'label', 'name',
    ],
  },
  {
    key: 'riskCategoryOptions',
    getMasterRows: getRawRiskCategoryRowsFromPayload,
    getFallbackRows: ({ risks, infoSecRisks }) => [...risks, ...infoSecRisks],
    idKeys: [
      'category_id', 'categoryId', 'asset_category_id', 'assetCategoryId',
      'risk_category_id', 'riskCategoryId', 'id',
    ],
    nameKeys: [
      'category_name', 'categoryName', 'asset_category_name', 'assetCategoryName',
      'risk_category_name', 'riskCategoryName', 'name', 'category',
    ],
  },
  {
    key: 'riskImpactOptions',
    getMasterRows: getRawRiskImpactRowsFromPayload,
    getFallbackRows: ({ risks, infoSecRisks }) => [...risks, ...infoSecRisks],
    idKeys: ['impact_id', 'impactId', 'id'],
    nameKeys: ['impact_name', 'impactName', 'name', 'impact'],
  },
  {
    key: 'riskLikelihoodOptions',
    getMasterRows: getRawRiskLikelihoodRowsFromPayload,
    getFallbackRows: ({ risks, infoSecRisks }) => [...risks, ...infoSecRisks],
    idKeys: ['likelihood_id', 'likelihoodId', 'id'],
    nameKeys: ['likelihood_name', 'likelihoodName', 'name', 'likelihood'],
  },
  {
    key: 'riskMitigationOptions',
    getMasterRows: getRawRiskMitigationRowsFromPayload,
    getFallbackRows: ({ risks, infoSecRisks }) => [...risks, ...infoSecRisks],
    idKeys: ['mitigation_strategy_id', 'mitigationStrategyId', 'id'],
    nameKeys: [
      'mitigation_strategy_value', 'mitigationStrategyValue',
      'mitigation_strategy_name', 'mitigationStrategyName', 'name', 'mitigation',
    ],
  },
  {
    key: 'riskStatusOptions',
    getMasterRows: getRawRiskStatusRowsFromPayload,
    getFallbackRows: ({ risks, infoSecRisks }) => [...risks, ...infoSecRisks],
    idKeys: ['risk_status_id', 'riskStatusId', 'status_id', 'statusId', 'id'],
    nameKeys: ['risk_status_name', 'riskStatusName', 'status_name', 'statusName', 'name', 'status', 'riskStatus'],
  },
  {
    key: 'issueStatusOptions',
    getMasterRows: getRawIssueStatusRowsFromPayload,
    getFallbackRows: ({ issues }) => issues,
    idKeys: ['issue_status_id'],
    nameKeys: ['label'],
  },
  {
    key: 'issueCategoryOptions',
    getMasterRows: getRawIssueCategoryRowsFromPayload,
    getFallbackRows: ({ issues }) => issues,
    idKeys: ['issue_category_id'],
    nameKeys: ['issue_category_name'],
  },
];

const getRawRiskTabRowsFromPayload = (payload, tabKey) => {
  if (!payload || typeof payload !== 'object') return [];

  const candidates = tabKey === 'risk'
    ? [
      getNestedValue(payload, 'riskOnlyRegister'),
      getNestedValue(payload, 'data.riskOnlyRegister'),
      getNestedValue(payload, 'risk_only_register'),
      getNestedValue(payload, 'data.risk_only_register'),
    ]
    : tabKey === 'issue'
      ? [
        getNestedValue(payload, 'issueRegister'),
        getNestedValue(payload, 'data.issueRegister'),
        getNestedValue(payload, 'issue_register'),
        getNestedValue(payload, 'data.issue_register'),
      ]
      : [
        getNestedValue(payload, 'infoSecRiskRegister'),
        getNestedValue(payload, 'data.infoSecRiskRegister'),
        getNestedValue(payload, 'info_sec_risk_register'),
        getNestedValue(payload, 'data.info_sec_risk_register'),
        getNestedValue(payload, 'infosecRiskRegister'),
        getNestedValue(payload, 'data.infosecRiskRegister'),
      ];

  const toRows = (candidate) => {
    if (Array.isArray(candidate)) return candidate;
    if (!candidate || typeof candidate !== 'object') return null;
    return firstArray([candidate.rows, candidate.data, candidate.items, candidate.list, candidate.values]);
  };

  return candidates.map(toRows).find(Array.isArray) || [];
};

const buildRiskRegisterData = (baseData) => {
  const mapRows = (rows, entityType) => rows
    .filter((row) => row && typeof row === 'object')
    .map((row, index) => mapRiskRowToUi(row, index, entityType));

  const risks = mapRows(getRawRiskTabRowsFromPayload(baseData, 'risk'), 'risk');
  const issues = mapRows(getRawRiskTabRowsFromPayload(baseData, 'issue'), 'issue');
  const infoSecRisks = mapRows(getRawRiskTabRowsFromPayload(baseData, 'infosec'), 'infosec');
  const riskOptions = RISK_OPTION_BUILDERS.reduce((acc, config) => ({
    ...acc,
    [config.key]: buildRiskOptionsFromMaster({
      masterRows: config.getMasterRows(baseData),
      fallbackRows: config.getFallbackRows({ risks, issues, infoSecRisks }),
      idKeys: config.idKeys,
      nameKeys: config.nameKeys,
    }),
  }), {});

  return {
    summary: {
      total: 0,
      open: 0,
      closed: 0,
      escalated: 0,
      high: 0,
      medium: 0,
      low: 0,
      overdue: 0,
    },
    risks,
    issues,
    infoSecRisks,
    ...riskOptions,
    tabs: ['Risk', 'Issue', 'InfoSec Risk'],
  };
};

export default buildRiskRegisterData;
